import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { analyzeParameters, analyzeParametersFromAPI } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ──────────────────────────────────── */
/* Parameters Analysis — full port     */
/* ──────────────────────────────────── */

const COLORS = ['#818CF8', '#F87171', '#34D399', '#FBBF24', '#60A5FA', '#A78BFA', '#FB923C'];

function ParametersAnalysis() {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [portfolio, setPortfolio] = useState('');
    const [targetAcos, setTargetAcos] = useState('30'); // Changed to string to match input type

    // Dual-mode: 'file' or 'api'
    const [dataSource, setDataSource] = useState('file');
    const [startDate, setStartDate] = useState(() => { var d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

    const fileRef = useRef(file);
    // targetAcosRef is no longer needed as targetAcos state is used directly
    const hasResult = useRef(false);
    useEffect(() => { fileRef.current = file; }, [file]);
    // useEffect for targetAcosRef is no longer needed

    const doAnalyze = useCallback(async (pOverride, aOverride) => {
        if (dataSource === 'file') {
            const f = fileRef.current;
            if (!f) {
                setError('Please select a file to analyze.');
                return;
            }
        }
        if (dataSource === 'api') {
            const profileId = localStorage.getItem('selectedAccountValue');
            if (!profileId) {
                setError('Please select an Amazon Ads account in the header before using API mode.');
                return;
            }
        }
        setLoading(true);
        setError('');
        try {
            const pVal = pOverride !== undefined ? pOverride : (portfolio || undefined);
            const aVal = aOverride !== undefined ? Number(aOverride) : Number(targetAcos); // Ensure targetAcos is a number
            let res;
            if (dataSource === 'api') {
                const profileId = localStorage.getItem('selectedAccountValue');
                res = await analyzeParametersFromAPI(profileId, startDate, endDate, aVal, pVal);
            } else {
                res = await analyzeParameters(fileRef.current, aVal, pVal);
            }
            setResult(res.data);
            hasResult.current = true;
        } catch (err) {
            setError(err.response && err.response.data ? err.response.data.message : err.message);
        } finally {
            setLoading(false);
        }
    }, [portfolio, targetAcos, dataSource, startDate, endDate, setError, setLoading, setResult]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (dataSource === 'file' && !fileRef.current) {
            setError('Please select a file to analyze.');
            return;
        }
        doAnalyze();
    };

    const handlePortfolioChange = (val) => {
        setPortfolio(val);
        if (hasResult.current) { // No need to check fileRef.current here, as doAnalyze handles it
            setTimeout(() => doAnalyze(val, targetAcos), 50);
        }
    };

    const handleAcosChange = (val) => {
        setTargetAcos(val);
        if (hasResult.current) {
            setTimeout(() => doAnalyze(portfolio, val), 50);
        }
    };

    const charts = result ? result.placementCharts : null;

    /* ── Helper to build stacked bar traces ── */
    const buildStackedTraces = (data, yKey) => {
        if (!data || data.length === 0) return [];
        const placements = [...new Set(data.map(r => r.placement))];
        return placements.map((p, i) => {
            const subset = data.filter(r => r.placement === p);
            return {
                x: subset.map(r => r.date),
                y: subset.map(r => r[yKey]),
                type: 'bar',
                name: p,
                marker: { color: COLORS[i % COLORS.length] },
            };
        });
    };

    /* ── Helper to build line traces ── */
    const buildLineTraces = (data, yKey) => {
        if (!data || data.length === 0) return [];
        const placements = [...new Set(data.map(r => r.placement))];
        return placements.map((p, i) => {
            const subset = data.filter(r => r.placement === p);
            return {
                x: subset.map(r => r.date),
                y: subset.map(r => r[yKey]),
                type: 'scatter',
                mode: 'lines+markers',
                name: p,
                line: { color: COLORS[i % COLORS.length], width: 2 },
                marker: { size: 4 },
            };
        });
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Campaign Parameters Analysis</h2>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* Upload / API */}
                <div className="card mb-4">
                    <div className="card-body">
                        {/* Mode Toggle */}
                        <div className="btn-group mb-3" role="group">
                            <button type="button"
                                className={`btn ${dataSource === 'file' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('file')}>
                                <i className="bi bi-file-earmark-arrow-up me-1"></i> Upload File
                            </button>
                            <button type="button"
                                className={`btn ${dataSource === 'api' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('api')}>
                                <i className="bi bi-cloud-download me-1"></i> Fetch from Amazon API
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 align-items-end">
                                {dataSource === 'file' ? (
                                    <div className="col-md-3">
                                        <label className="form-label">Campaign Report (Excel)</label>
                                        <input type="file" className="form-control" accept=".xlsx,.xls,.csv"
                                            onChange={(e) => setFile(e.target.files[0])} required={dataSource === 'file'} />
                                    </div>
                                ) : (
                                    <>
                                        <div className="col-md-2">
                                            <label className="form-label">Start Date</label>
                                            <input type="date" className="form-control" value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)} required={dataSource === 'api'} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">End Date</label>
                                            <input type="date" className="form-control" value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)} required={dataSource === 'api'} />
                                        </div>
                                    </>
                                )}
                                <div className="col-md-3">
                                    <label className="form-label">Portfolio (optional)</label>
                                    {result && result.portfolios ? (
                                        <select className="form-select" value={portfolio}
                                            onChange={(e) => handlePortfolioChange(e.target.value)}>
                                            <option value="">All portfolios</option>
                                            {result.portfolios.map((p) => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    ) : (
                                        <input className="form-control" value={portfolio}
                                            onChange={(e) => setPortfolio(e.target.value)} placeholder="Filter by portfolio..." />
                                    )}
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Target ACOS %</label>
                                    <input type="number" className="form-control" value={targetAcos}
                                        onChange={(e) => handleAcosChange(e.target.value)} min={1} max={100} />
                                </div>
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-primary w-100"
                                        disabled={loading || (dataSource === 'file' && !file)}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : 'Analyze'}
                                    </button>
                                </div>
                            </div>
                            {dataSource === 'api' && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Account: <strong>{localStorage.getItem('selectedAccountName') || 'Not selected'}</strong>
                                    </small>
                                </div>
                            )}
                        </form>
                    </div>
                </div>

                {/* Base metrics */}
                {result && result.baseMetrics && result.baseMetrics.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Base Metrics per ASIN</h5>
                            <div className="table-responsive">
                                <table className="table table-sm table-striped">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>ASIN</th><th>Spend</th><th>Sales</th><th>Orders</th>
                                            <th>AOV</th><th>Target CPA</th><th>CVR%</th><th>Max CPC</th>
                                            <th>Current ACOS</th><th>Target ACOS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.baseMetrics.map(m => (
                                            <tr key={m.asin}>
                                                <td className="fw-bold">{m.asin}</td>
                                                <td>${m.totalSpend.toFixed(2)}</td>
                                                <td>${m.totalSales.toFixed(2)}</td>
                                                <td>{m.totalOrders}</td>
                                                <td>${m.aov.toFixed(2)}</td>
                                                <td>${m.targetCpa.toFixed(2)}</td>
                                                <td>{m.cvr.toFixed(2)}%</td>
                                                <td>${m.maxCpc.toFixed(2)}</td>
                                                <td className={m.currentAcos > targetAcos ? 'text-danger fw-bold' : 'text-success'}>
                                                    {m.currentAcos.toFixed(2)}%
                                                </td>
                                                <td>{m.targetAcos}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Match type charts */}
                {result && result.matchTypes && result.matchTypes.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Match Type Analysis</h5>
                            <div className="row">
                                {result.matchTypes.map(mt => (
                                    <div className="col-md-6" key={mt.asin}>
                                        <h6>{mt.asin}</h6>
                                        <Plot
                                            data={[{
                                                x: mt.breakdown.map(b => b.matchType),
                                                y: mt.breakdown.map(b => b.spend),
                                                type: 'bar', name: 'Spend',
                                                marker: { color: '#EF4444' },
                                            }, {
                                                x: mt.breakdown.map(b => b.matchType),
                                                y: mt.breakdown.map(b => b.sales),
                                                type: 'bar', name: 'Sales',
                                                marker: { color: '#22C55E' },
                                            }]}
                                            layout={{
                                                height: 300, barmode: 'group',
                                                margin: { l: 60, r: 10, t: 10, b: 60 },
                                                legend: { orientation: 'h' },
                                                xaxis: { title: { text: 'Match Type' } },
                                                yaxis: { title: { text: 'Amount (USD)' } },
                                            }}
                                            config={{ responsive: true, displayModeBar: false }}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Placement breakdown with New Bid */}
                {result && result.placements && result.placements.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Placement Analysis &amp; Bid Recommendations</h5>
                            {result.placements.map(p => (
                                <div key={p.asin} className="mb-4">
                                    <h6>{p.asin} — AOV: ${p.aov} | Target CPA: ${p.targetCpa}</h6>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Placement</th><th>Spend</th><th>Sales</th><th>Orders</th>
                                                    <th>ACOS%</th><th>CVR%</th><th>Current CPC</th>
                                                    <th className="bg-warning bg-opacity-25">New Bid</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {p.placementBreakdown.map((pb, i) => (
                                                    <tr key={i}>
                                                        <td>{pb.placement}</td>
                                                        <td>${pb.spend.toFixed(2)}</td>
                                                        <td>${pb.sales.toFixed(2)}</td>
                                                        <td>{pb.orders}</td>
                                                        <td className={pb.acos > targetAcos ? 'text-danger' : 'text-success'}>{pb.acos.toFixed(2)}%</td>
                                                        <td>{pb.cvr.toFixed(2)}%</td>
                                                        <td>${pb.currentCpc.toFixed(2)}</td>
                                                        <td className="fw-bold bg-warning bg-opacity-25">${pb.newBid.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ═══════════ PLACEMENT CHARTS ═══════════ */}
                {charts && (
                    <>
                        {/* 1) Click Distribution by Placement */}
                        {charts.clicksByPlacement && charts.clicksByPlacement.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[{
                                            x: charts.clicksByPlacement.map(r => r.placement),
                                            y: charts.clicksByPlacement.map(r => r.clicks),
                                            type: 'bar',
                                            marker: { color: COLORS[0], opacity: 0.85 },
                                            hovertemplate: '<b>%{x}</b><br>Clicks: %{y:,}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Click Distribution by Placement', font: { size: 16 } },
                                            xaxis: { title: { text: 'Placement' }, tickangle: -30 },
                                            yaxis: { title: { text: 'Total Clicks' } },
                                            margin: { l: 60, r: 20, t: 50, b: 120 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 2) Orders Distribution by Placement */}
                        {charts.ordersByPlacement && charts.ordersByPlacement.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[{
                                            x: charts.ordersByPlacement.map(r => r.placement),
                                            y: charts.ordersByPlacement.map(r => r.orders),
                                            type: 'bar',
                                            marker: { color: COLORS[0], opacity: 0.85 },
                                            hovertemplate: '<b>%{x}</b><br>Orders: %{y:,}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Orders Distribution by Placement', font: { size: 16 } },
                                            xaxis: { title: { text: 'Placement' }, tickangle: -30 },
                                            yaxis: { title: { text: 'Total Orders (7 Days)' } },
                                            margin: { l: 60, r: 20, t: 50, b: 120 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 3) Clicks vs Orders % Distribution */}
                        {charts.clicksVsOrdersPct && charts.clicksVsOrdersPct.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[
                                            {
                                                x: charts.clicksVsOrdersPct.map(r => r.placement),
                                                y: charts.clicksVsOrdersPct.map(r => r.clicksPct),
                                                type: 'bar', name: 'Clicks Percentage',
                                                marker: { color: COLORS[0] },
                                            },
                                            {
                                                x: charts.clicksVsOrdersPct.map(r => r.placement),
                                                y: charts.clicksVsOrdersPct.map(r => r.ordersPct),
                                                type: 'bar', name: 'Orders Percentage',
                                                marker: { color: COLORS[1] },
                                            },
                                        ]}
                                        layout={{
                                            title: { text: 'Clicks vs Orders Percentage Distribution by Placement', font: { size: 16 } },
                                            barmode: 'group',
                                            xaxis: { title: { text: 'Placement' }, tickangle: -30 },
                                            yaxis: { title: { text: 'Percentage (%)' } },
                                            legend: { title: { text: 'Metric' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 150, t: 50, b: 120 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 4) Orders Evolution by Placement Over Time */}
                        {charts.ordersByDate && charts.ordersByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildLineTraces(charts.ordersByDate, 'orders')}
                                        layout={{
                                            title: { text: 'Orders Evolution by Placement Over Time', font: { size: 16 } },
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Total Orders (7 Days)' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 5) Evolution of Spend % by Placement (stacked bar) */}
                        {charts.spendPctByDate && charts.spendPctByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildStackedTraces(charts.spendPctByDate, 'spendPct')}
                                        layout={{
                                            title: { text: 'Evolution of Spend Percentage by Placement Over Time', font: { size: 16 } },
                                            barmode: 'stack',
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Spend Percentage (%)' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 6) Evolution of Clicks % by Placement (stacked bar) */}
                        {charts.clicksPctByDate && charts.clicksPctByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildStackedTraces(charts.clicksPctByDate, 'clicksPct')}
                                        layout={{
                                            title: { text: 'Evolution of Clicks Percentage by Placement Over Time', font: { size: 16 } },
                                            barmode: 'stack',
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Clicks Percentage (%)' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 7) Top Campaigns by Clicks in Product Pages */}
                        {charts.topCampaignsByClicks && charts.topCampaignsByClicks.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={[{
                                            y: charts.topCampaignsByClicks.map(r => r.campaign).reverse(),
                                            x: charts.topCampaignsByClicks.map(r => r.clicks).reverse(),
                                            type: 'bar',
                                            orientation: 'h',
                                            marker: {
                                                color: charts.topCampaignsByClicks.map(r => r.clicks).reverse(),
                                                colorscale: 'Viridis',
                                            },
                                            hovertemplate: '<b>%{y}</b><br>Clicks: %{x:,}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Campaigns with Highest Clicks in Product Pages', font: { size: 16 } },
                                            xaxis: { title: { text: 'Total Clicks' } },
                                            yaxis: { title: { text: 'Campaign Name' }, automargin: true },
                                            margin: { l: 250, r: 20, t: 50, b: 60 },
                                            height: Math.max(400, charts.topCampaignsByClicks.length * 28 + 100),
                                            showlegend: false,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 8) Top Campaigns by Spend in Product Pages */}
                        {charts.topCampaignsBySpend && charts.topCampaignsBySpend.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h5>Top Spend in Product Pages (&gt; $10)</h5>
                                    <div className="table-responsive mb-3">
                                        <table className="table table-sm table-striped">
                                            <thead className="table-dark">
                                                <tr><th>Campaign Name</th><th>Total Spend</th></tr>
                                            </thead>
                                            <tbody>
                                                {charts.topCampaignsBySpend.map((r, i) => (
                                                    <tr key={i}>
                                                        <td>{r.campaign}</td>
                                                        <td>${r.spend.toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Plot
                                        data={[{
                                            y: charts.topCampaignsBySpend.map(r => r.campaign).reverse(),
                                            x: charts.topCampaignsBySpend.map(r => r.spend).reverse(),
                                            type: 'bar',
                                            orientation: 'h',
                                            marker: {
                                                color: charts.topCampaignsBySpend.map(r => r.spend).reverse(),
                                                colorscale: 'Viridis',
                                            },
                                            hovertemplate: '<b>%{y}</b><br>Spend: $%{x:,.2f}<extra></extra>',
                                        }]}
                                        layout={{
                                            title: { text: 'Campaigns with Highest Spend in Product Pages (> $10)', font: { size: 16 } },
                                            xaxis: { title: { text: 'Total Spend' } },
                                            yaxis: { title: { text: 'Campaign Name' }, automargin: true },
                                            margin: { l: 250, r: 20, t: 50, b: 60 },
                                            height: Math.max(400, charts.topCampaignsBySpend.length * 28 + 100),
                                            showlegend: false,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* 9) Spend Evolution by Placement Over Time (line) */}
                        {charts.spendByDate && charts.spendByDate.length > 0 && (
                            <div className="card mb-4">
                                <div className="card-body">
                                    <Plot
                                        data={buildLineTraces(charts.spendByDate, 'spend')}
                                        layout={{
                                            title: { text: 'Spend Evolution by Placement Over Time', font: { size: 16 } },
                                            xaxis: { title: { text: 'Date' }, type: 'date' },
                                            yaxis: { title: { text: 'Total Spend' } },
                                            legend: { title: { text: 'Placement' }, orientation: 'v', x: 1.02, y: 1 },
                                            margin: { l: 60, r: 170, t: 50, b: 60 },
                                            height: 400,
                                            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true }}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}

export default ParametersAnalysis;
